import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ShieldCheck, ExternalLink, Loader2 } from 'lucide-react';

import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

// Simple case-insensitive substring match, no AI — just a transparent,
// explainable heads-up that a cart item name may relate to a recorded allergy.
function matchAllergies(allergies, cartItems) {
  const hits = [];
  (allergies || []).forEach((allergy) => {
    const a = (allergy || '').trim().toLowerCase();
    if (!a) return;
    (cartItems || []).forEach((item) => {
      const name = (item.product_name || '').toLowerCase();
      const generic = (item.generic_name || '').toLowerCase();
      if ((name && (name.includes(a) || a.includes(name))) || (generic && (generic.includes(a) || a.includes(generic)))) {
        hits.push({ allergy, item: item.product_name });
      }
    });
  });
  return hits;
}

const ClinicalDetailReview = ({ patientId, cartItems, onReady }) => {
  const [plain, setPlain] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    setBusy(true);
    api.get(`/care/customers/${patientId}/clinical-profile`).then((r) => {
      const row = r.data.profile;
      if (!live) return;
      const data = row?.profile || {};
      setPlain(data);
      onReady({ matches: matchAllergies(data.allergies, cartItems), g6pd: data.g6pd_status, shared: !!row });
    }).catch((e) => { if (live) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  if (busy) return <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="clinical-override-decrypting"><Loader2 className="w-3 h-3 animate-spin" />Loading clinical profile…</p>;
  if (err) return <p className="text-xs text-red-600" data-testid="clinical-override-decrypt-error">{err}</p>;
  if (!plain || !Object.keys(plain).length) return <p className="text-xs text-muted-foreground" data-testid="clinical-override-empty">No clinical profile shared for this branch.</p>;
  return (
    <div className="text-xs space-y-1" data-testid="clinical-override-detail">
      <p>G6PD status: <b data-testid="clinical-override-g6pd">{plain.g6pd_status || 'Unknown'}</b></p>
      <p>Allergies on file: <b data-testid="clinical-override-allergies">{(plain.allergies || []).join(', ') || 'None recorded'}</b></p>
    </div>
  );
};

export const ClinicalSafetyGate = ({ phone, cart, onBlockingChange }) => {
  const { user } = useAuth();
  const [flag, setFlag] = useState(null);
  const [overridden, setOverridden] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pharmEmail, setPharmEmail] = useState('');
  const [pharmPassword, setPharmPassword] = useState('');
  const [note, setNote] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [matchInfo, setMatchInfo] = useState(null);
  const onBlockingChangeRef = useRef(onBlockingChange);
  onBlockingChangeRef.current = onBlockingChange;

  useEffect(() => {
    const p = (phone || '').trim();
    if (!p || p.length < 6) { setFlag(null); setOverridden(false); return; }
    const timer = setTimeout(() => {
      api.get('/care/customers/by-phone/clinical-flag', { params: { phone: p } })
        .then((r) => { setFlag(r.data); setOverridden(false); setMatchInfo(null); })
        .catch(() => setFlag(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [phone]);

  useEffect(() => { onBlockingChangeRef.current(!!(flag?.shared && !overridden)); }, [flag, overridden]);

  if (!flag?.shared) return null;

  const isPharmacist = user?.role === 'pharmacist' || user?.role === 'super_admin';

  const confirmSelfOverride = async () => {
    setVerifyBusy(true); setVerifyError('');
    try {
      await api.post('/care/checkout-override', {
        phone, patient_user_id: flag.patient_user_id, pharmacist_user_id: user.user_id,
        note, matched_allergies: (matchInfo?.matches || []).map((m) => m.allergy),
      });
      setOverridden(true); setModalOpen(false);
    } catch (e) { setVerifyError(e?.response?.data?.detail || 'Could not record override'); }
    finally { setVerifyBusy(false); }
  };

  const verifyAndOverride = async () => {
    setVerifyBusy(true); setVerifyError('');
    try {
      const r = await api.post('/care/verify-pharmacist', { email: pharmEmail.trim(), password: pharmPassword });
      if (!r.data.ok) { setVerifyError(r.data.error || 'Pharmacist verification failed'); return; }
      await api.post('/care/checkout-override', { phone, patient_user_id: flag.patient_user_id, pharmacist_user_id: r.data.pharmacist_user_id, note });
      setOverridden(true); setModalOpen(false);
    } catch (e) { setVerifyError(e?.response?.data?.detail || 'Pharmacist verification failed'); }
    finally { setVerifyBusy(false); }
  };

  return (
    <>
      <Alert className="border-amber-500 bg-amber-50 text-amber-900" data-testid="clinical-safety-banner">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Clinical profile on file</AlertTitle>
        <AlertDescription className="text-xs space-y-2">
          <p>This customer has a shared clinical profile — review before dispensing.</p>
          <div className="flex gap-2 flex-wrap items-center">
            <a href={`/customers/records?patient=${flag.patient_user_id}`} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" data-testid="clinical-safety-review-link">
              <ExternalLink className="w-3 h-3" />Review in Customer Workspace
            </a>
            <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => setModalOpen(true)} data-testid="clinical-safety-override-btn">
              Pharmacist override
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm" data-testid="clinical-override-dialog">
          <DialogHeader><DialogTitle>Pharmacist override required</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            {isPharmacist ? (
              <>
                <ClinicalDetailReview patientId={flag.patient_user_id} cartItems={cart} onReady={setMatchInfo} />
                {matchInfo?.matches?.length > 0 && (
                  <Alert variant="destructive" data-testid="clinical-override-matches">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Possible match: {matchInfo.matches.map((m) => `${m.item} ↔ ${m.allergy}`).join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
                <div><Label>Override note</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="clinical-override-note" /></div>
                {verifyError && <p className="text-xs text-red-600" data-testid="clinical-override-error">{verifyError}</p>}
                <Button className="w-full" onClick={confirmSelfOverride} disabled={verifyBusy} data-testid="clinical-override-confirm-self">
                  {verifyBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                  I&apos;ve reviewed this — continue sale
                </Button>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">Ask a pharmacist on duty to authorize this sale.</p>
                <div><Label>Pharmacist email</Label><Input value={pharmEmail} onChange={(e) => setPharmEmail(e.target.value)} data-testid="clinical-override-pharm-email" /></div>
                <div><Label>Pharmacist password</Label><Input type="password" value={pharmPassword} onChange={(e) => setPharmPassword(e.target.value)} data-testid="clinical-override-pharm-password" /></div>
                <div><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="clinical-override-note" /></div>
                {verifyError && <p className="text-xs text-red-600" data-testid="clinical-override-error">{verifyError}</p>}
                <Button className="w-full" onClick={verifyAndOverride} disabled={verifyBusy || !pharmEmail || !pharmPassword} data-testid="clinical-override-confirm-verify">
                  {verifyBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                  Verify &amp; authorize
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
